import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, MessageCircle, Plus, User, Users, type LucideIcon } from 'lucide-react';

type NavItem = { path: string; label: string; Icon: LucideIcon; center?: boolean };

const ITEMS: NavItem[] = [
  { path: '/feed', label: 'Home', Icon: Home },
  { path: '/friends', label: 'Friends', Icon: Users },
  { path: '/upload', label: 'Create', Icon: Plus, center: true },
  { path: '/inbox', label: 'Inbox', Icon: MessageCircle },
  { path: '/profile', label: 'Profile', Icon: User },
];

const ICON_SIZE = 26;

function isActive(pathname: string, path: string): boolean {
  if (path === '/feed') return pathname === '/feed' || pathname === '/';
  if (path === '/upload') return pathname === '/upload' || pathname === '/create';
  if (path === '/profile') return pathname === '/profile' || pathname.startsWith('/profile/');
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Main bottom tab bar — same 480px column as the feed (live app). */
export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  const onPress = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  if (location.pathname === '/live' || location.pathname.startsWith('/live/')) return null;

  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[10002] bg-transparent"
      aria-label="Main navigation"
    >
      <div className="pointer-events-none flex justify-center">
        <div className="feed-column-width pointer-events-auto border-0 bg-transparent">
          <div
            className="flex items-center justify-around px-1 pt-1.5"
            style={{ paddingBottom: 'max(2px, env(safe-area-inset-bottom, 0px))' }}
          >
            {ITEMS.map(({ path, label, Icon, center }) => {
              const active = isActive(location.pathname, path);
              const color = active ? '#FFFFFF' : '#C8CDD5';
              const labelColor = active || center ? '#FFFFFF' : '#A7ABB2';

              return (
                <button
                  key={path}
                  type="button"
                  onClick={() => onPress(path)}
                  aria-label={label}
                  aria-current={active ? 'page' : undefined}
                  className="flex flex-1 flex-col items-center justify-center gap-0.5 active:opacity-75"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {center ? (
                    <span className="royce-glow-disc" aria-hidden="true">
                      <Plus className="relative z-[2] h-5 w-5" style={{ color: '#E8EAED' }} />
                    </span>
                  ) : (
                    <span className="royce-glow-disc" aria-hidden="true">
                      <Icon
                        size={ICON_SIZE}
                        strokeWidth={active ? 2.35 : 2}
                        className="relative z-[2]"
                        style={{ color, stroke: color }}
                      />
                    </span>
                  )}
                  <span
                    className="text-[9px] font-semibold leading-none tracking-wide"
                    style={{ color: labelColor }}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
}
